import { useState, useEffect, useRef } from 'react'
import { useOutletContext, useSearchParams, useParams } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { isNavigationClick, navigationShare } from '@/lib/analytics/clickRegion'
import { getAggregateClicksBreakdown, getClicksDetails, type ClicksBreakdownPage, type ClicksDetailsResponse, type ClickTrendBucket } from '@/lib/api/external-api'
import { interceptDemoAPI, demoGenerators } from '@/lib/utils/demoData'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useFilter } from '@/lib/contexts/FilterContext'
import { useWorkspaceSafe } from '@/lib/contexts/WorkspaceContext'
import { useURLState } from '@/hooks/useURLState'
import { formatNumeric } from '@/lib/utils/stringUtils'
import { formatTrendBucket, resolveTrendBucketFormat } from '@/lib/utils/timeUtils'
import { matchesPageSearch } from '@/lib/utils/pagePath'
import { CTR_TOOLTIP } from '@/lib/constants/metricsCopy'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import AnalyticsTabBar from '@/components/dashboard/AnalyticsTabBar'
import FormsSection from './FormsSection'
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { LoadingState } from '@/components/shared/layout'
import {
  Verdict,
  RaisedCard,
  CardHead,
  CardEmpty,
  FilterChip,
  Pagination,
} from '@/components/dashboard/analyticsTabs/TabPrimitives'
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Search01Icon,
  ArrowDown01Icon,
  Link01Icon,
  Cursor02Icon,
  Menu01Icon,
  MouseLeftClick01Icon,
  InformationCircleIcon,
} from "@hugeicons/core-free-icons"
import { isGenericTarget } from '@/components/heatmaps/heatmapInsights'
import { sentenceLabel } from '@/components/shared/analytics/PagesReport'

interface DashboardContext {
  propertyId?: string
  token?: string
  period?: string
  filters?: any[]
  dashboardData?: any
}

// Convert frontend period format to external API format
const toExternalApiPeriod = (period: string): string => {
  // Handle custom date range format: "YYYY-MM-DD:YYYY-MM-DD"
  if (period.includes(':')) {
    const [start, end] = period.split(':')
    // Calculate the difference to determine granularity
    const startDate = new Date(start)
    const endDate = new Date(end)
    const diffDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    
    // Choose granularity based on date range length
    let granularity = 'day'
    if (diffDays <= 2) {
      granularity = 'hour'
    } else if (diffDays > 90) {
      granularity = 'week'
    }
    
    // Format: granularity_CUSTOM_start_to_end (matching API expectation)
    return `${granularity}_CUSTOM_${start}_to_${end}`
  }

  const periodMap: Record<string, string> = {
    '1d': 'hour_PAST_24_H',
    '7d': 'day_PAST_7_D',
    '30d': 'day_PAST_30_D',
    '90d': 'day_PAST_3_M',
    '180d': 'week_PAST_6_M',
    '365d': 'week_PAST_12_M',
    '12m': 'month_PAST_12_M',
    'ytd': 'day_PAST_12_M',
    'mtd': 'day_PAST_30_D',
  }
  return periodMap[period] || 'day_PAST_7_D'
}

const clicksChartConfig: ChartConfig = {
  clicks: { label: 'Clicks', color: 'var(--chart-1)' },
  sessions: { label: 'Unique Visitors', color: 'var(--chart-2)' },
}

/**
 * The page breakdown carries only the target, so a generic component name
 * ("Link") is every link without a data-humblytics id on that page, not one
 * element. Say that instead of printing "Link".
 */
const targetLabel = (target?: string | null) =>
  !target ? '-' : isGenericTarget(target) ? 'Links without an id' : target

function ClicksTrendChart({ trendData, period }: { trendData: ClickTrendBucket[]; period?: string }) {
  const labelFormat = resolveTrendBucketFormat(
    trendData.map(b => b.bucket_start),
    period ? toExternalApiPeriod(period).split('_')[0] : null
  )
  const chartData = trendData.map(bucket => ({
    time: formatTrendBucket(bucket.bucket_start, labelFormat),
    clicks: bucket.clicks,
    sessions: bucket.unique_sessions,
  }))

  return (
    <div className="w-full">
      <ChartContainer config={clicksChartConfig} className="aspect-auto w-full" style={{ height: 220 }}>
        <RechartsBarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barGap={2} barCategoryGap="20%">
          <CartesianGrid
            vertical={false}
            horizontal={true}
            stroke="var(--border)"
          />
          <XAxis
            dataKey="time"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={32}
            tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
          />
          {/* One axis: clicks and visitors are both counts. Two scales made 30
              visitors draw taller than 1,000 clicks. */}
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={4}
            width={36}
            tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
            tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
          />
          <ChartTooltip
            cursor={{ fill: 'var(--muted)', opacity: 0.6 }}
            content={
              <ChartTooltipContent
                labelFormatter={(value) => String(value)}
                indicator="dot"
              />
            }
          />
          <Bar
            dataKey="clicks"
            fill="var(--chart-1)"
            radius={[3, 3, 0, 0]}
            maxBarSize={32}
          />
          <Bar
            dataKey="sessions"
            fill="var(--chart-2)"
            radius={[3, 3, 0, 0]}
            maxBarSize={32}
          />
        </RechartsBarChart>
      </ChartContainer>
    </div>
  )
}

/** "Clicks & forms" tab: the clicks well, then the form submissions well. */
export default function ClicksDashboard() {
  const ctx = useOutletContext<DashboardContext>()
  const params = useParams()
  const { token: authToken } = useAuth()
  const { filters: contextFilters } = useFilter()
  const { state } = useURLState()
  
  // Use token from context (share mode) or auth (regular mode)
  const token = ctx.token || authToken
  const workspaceContext = useWorkspaceSafe()
  // Use all selected property IDs from workspace context; fall back to single property from outlet (share/legacy routes)
  const propertyIds = workspaceContext?.selectedPropertyIds?.length
    ? workspaceContext.selectedPropertyIds
    : ctx.propertyId ? [ctx.propertyId] : []
  
  // Get the base path for tab navigation
  const getBasePath = () => {
    if (params.magicId && params.password) {
      return `/s/${params.magicId}/${params.password}`;
    }
    if (params.workspaceId) {
      return `/workspace/${params.workspaceId}`;
    }
    if (params.propertySlugId) {
      return `/p/${params.propertySlugId}`;
    }
    if (params.domain) {
      return `/d/${params.domain}`;
    }
    return '';
  };
  const basePath = getBasePath();
  const [searchParams, setSearchParams] = useSearchParams()
  const [clickData, setClickData] = useState<ClicksBreakdownPage[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [trendData, setTrendData] = useState<ClickTrendBucket[]>([])
  const [groupSummary, setGroupSummary] = useState<Record<string, { pages: number; total_clicks: number; unique_sessions: number }>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const isInitialLoad = useRef(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set(['all']))
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10
  const batchSize = 100

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Client filtering uses live searchTerm — reset page/groups immediately so a
  // stale page index or group chip can't hide matches while debounce catches up.
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
    setSelectedGroups((prev) => (prev.size === 1 && prev.has('all') ? prev : new Set(['all'])))
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value)
    }, 300)
  }

  // Detail drawer state
  const selectedPage = searchParams.get('detailPage')
  const [clickDetails, setClickDetails] = useState<ClicksDetailsResponse | null>(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [clicksOffset, setClicksOffset] = useState(0)
  const clicksLimit = 20

  useEffect(() => {
    const fetchData = async () => {
      if (!propertyIds.length || !token) {
        setIsLoading(false)
        return
      }

      if (isInitialLoad.current) {
        setIsLoading(true)
      } else {
        setIsRefreshing(true)
      }
      setCurrentPage(1)
      try {
        const externalApiPeriod = toExternalApiPeriod(state.period || '7d')

        const filterParams: Record<string, string> = {}
        for (const f of state.filters) {
          if (f.operator === ':') filterParams[f.field] = f.value
          else if (f.operator === '!=') filterParams[`exclude_${f.field}`] = f.value
        }

        const response = await interceptDemoAPI(
          propertyIds[0],
          () => getAggregateClicksBreakdown(propertyIds, externalApiPeriod, token, batchSize, ctx.propertyId, filterParams, debouncedSearch || undefined, 0),
          demoGenerators.clicks
        )

        const rows = response.data ?? []
        setClickData(rows)
        setTotalCount(response.meta.total_count ?? rows.length)
        setTrendData(response.trend || [])
        setGroupSummary(response.summary.groups || {})
      } catch (error) {
        console.error('Error fetching click stats:', error)
        setClickData([])
      } finally {
        setIsLoading(false)
        setIsRefreshing(false)
        isInitialLoad.current = false
      }
    }

    fetchData()
  }, [propertyIds.join(','), token, state.period, contextFilters, debouncedSearch])

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
    const requiredItems = newPage * itemsPerPage
    if (requiredItems > clickData.length && clickData.length < totalCount && !isFetchingMore) {
      setIsFetchingMore(true)
      const offset = clickData.length
      const externalApiPeriod = toExternalApiPeriod(state.period || '7d')
      const filterParams: Record<string, string> = {}
      for (const f of state.filters) {
        if (f.operator === ':') filterParams[f.field] = f.value
        else if (f.operator === '!=') filterParams[`exclude_${f.field}`] = f.value
      }
      getAggregateClicksBreakdown(propertyIds, externalApiPeriod, token!, batchSize, ctx.propertyId, filterParams, debouncedSearch || undefined, offset)
        .then((response) => {
          setClickData((prev) => [...prev, ...response.data])
          setTotalCount(response.meta.total_count ?? (offset + response.data.length))
        })
        .catch((error) => console.error('Error fetching more clicks:', error))
        .finally(() => setIsFetchingMore(false))
    }
  }

  // Fetch click details when a page is selected
  useEffect(() => {
    const fetchClickDetails = async () => {
      if (!selectedPage || !propertyIds.length || !token) {
        setClickDetails(null)
        return
      }

      setIsLoadingDetails(true)
      try {
        const externalApiPeriod = toExternalApiPeriod(state.period || '7d')
        const filterParams: Record<string, string> = {}
        for (const f of state.filters) {
          if (f.operator === ':') filterParams[f.field] = f.value
          else if (f.operator === '!=') filterParams[`exclude_${f.field}`] = f.value
        }
        const response = await getClicksDetails(selectedPage, externalApiPeriod, propertyIds[0], token, clicksLimit, clicksOffset, filterParams)
        setClickDetails(response)
      } catch (error) {
        console.error('Error fetching click details:', error)
        setClickDetails(null)
      } finally {
        setIsLoadingDetails(false)
      }
    }

    fetchClickDetails()
  }, [selectedPage, propertyIds[0], token, state.period, clicksOffset, JSON.stringify(state.filters)])

  const togglePageDetails = (page: string) => {
    const params = new URLSearchParams(searchParams)
    if (selectedPage === page) {
      // Close if already open
      params.delete('detailPage')
      setClicksOffset(0)
    } else {
      // Open new page
      params.set('detailPage', page)
      setClicksOffset(0)
    }
    setSearchParams(params)
  }

  // Toggle group filter
  const toggleGroup = (group: string) => {
    const newGroups = new Set(selectedGroups)

    if (group === 'all') {
      setSelectedGroups(new Set(['all']))
      setCurrentPage(1)
      return
    }

    newGroups.delete('all')

    if (newGroups.has(group)) {
      newGroups.delete(group)
      if (newGroups.size === 0) {
        newGroups.add('all')
      }
    } else {
      newGroups.add(group)
    }

    setSelectedGroups(newGroups)
    setCurrentPage(1)
  }

  // Get available groups sorted by total clicks
  const availableGroups = Object.entries(groupSummary)
    .sort((a, b) => b[1].total_clicks - a[1].total_clicks)
    .map(([group]) => group)

  // Filter by group chips + client search (also covers demo / backend gaps).
  const filteredData = clickData.filter((page) => {
    const groupOk = selectedGroups.has('all') || selectedGroups.has(page.page_group)
    const targetNames = page.top_targets?.map((t) => t.target) ?? []
    const searchOk = matchesPageSearch(page.page, searchTerm, [page.page_group, ...targetNames])
    return groupOk && searchOk
  })

  // Calculate summary metrics
  const totalClicks = filteredData.reduce((sum, page) => sum + page.total_clicks, 0)
  const totalSessions = ctx.dashboardData?.metrics?.totalSessions
    ?? filteredData.reduce((sum, page) => sum + page.unique_sessions, 0)
  // Navigation (a sticky navbar collects clicks on every page) vs clicks on
  // the page itself. From each page's top targets, so a floor on the real share.
  const navShare = navigationShare(filteredData.flatMap((page) => page.top_targets ?? []))
  const navPct = navShare != null ? Math.round(navShare * 100) : null

  // Get top target across all filtered pages
  const topTarget = (() => {
    const targetCounts: Record<string, number> = {}
    filteredData.forEach((page) => {
      page.top_targets.forEach((target) => {
        targetCounts[target.target] = (targetCounts[target.target] || 0) + target.clicks
      })
    })

    let maxTarget = ''
    let maxCount = 0
    Object.entries(targetCounts).forEach(([target, count]) => {
      if (count > maxCount) {
        maxCount = count
        maxTarget = target
      }
    })
    return maxTarget
  })()

  // Sort and paginate
  const sortedData = [...filteredData].sort((a, b) => b.total_clicks - a.total_clicks)
  const paginatedData = sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
  const isGroupFiltered = !selectedGroups.has('all')
  // Group chips filter client-side only; search is applied server-side (debouncedSearch),
  // so its total_count already reflects the filter. Trust the server total for search so
  // matches beyond the first loaded batch stay reachable.
  const paginationTotal = isGroupFiltered
    ? sortedData.length
    : Math.max(totalCount, sortedData.length)
  const totalPages = Math.ceil(paginationTotal / itemsPerPage)

  if (isLoading) {
    return (
      <LoadingState
        message="Loading clicks data..."
        size="lg"
        fullPage
      />
    )
  }

  // Clicks ÷ visitors is a count per visitor (1.55), not a rate: it passes 1 whenever people click twice.
  const ctr = totalSessions > 0 ? (totalClicks / totalSessions).toFixed(2) : '--'

  return (
    <div className="w-full">
      {/* Analytics Tab Navigation */}
      <AnalyticsTabBar basePath={basePath} className="mb-6" />

      <div className="flex flex-col gap-4">
        <Verdict
          title={
            totalClicks > 0
              ? `${formatNumeric(totalClicks)} clicks across ${filteredData.length} ${filteredData.length === 1 ? 'page' : 'pages'}.${topTarget ? ` The most clicked element is ${targetLabel(topTarget)}.` : ''}`
              : 'No clicks tracked for this period yet.'
          }
          meta={navPct != null && navPct > 0
            ? `Every tracked click on links and buttons. ${navPct}% of the top elements' clicks are on the navigation menu, the rest on the pages themselves.`
            : 'Every tracked click on links and buttons'}
        />

        {/* One well, raised cards (White signal v2) */}
        <div className="well flex flex-col gap-2">
          <RaisedCard>
            <div className="flex overflow-x-auto border-b border-border/60 scrollbar-hide">
              {[
                { label: 'Clicks', value: formatNumeric(totalClicks) },
                { label: 'Unique visitors', value: formatNumeric(totalSessions) },
                { label: 'Clicks per visitor', value: ctr },
                { label: 'Pages', value: filteredData.length },
                { label: 'On navigation', value: navPct != null ? `${navPct}%` : '--' },
                { label: 'Top target', value: topTarget ? targetLabel(topTarget) : '--', wide: true },
              ].map((tile, i) => (
                <div
                  key={tile.label}
                  className={`flex min-w-[140px] flex-col gap-1.5 px-[18px] py-4 ${tile.wide ? 'flex-[1.6]' : 'flex-1'} ${i > 0 ? 'border-l border-border/60' : ''}`}
                >
                  <span className="text-xs text-muted-foreground">{tile.label}</span>
                  <span className="truncate text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground tabular-nums" title={String(tile.value)}>
                    {tile.value}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 px-[18px] pt-3.5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="h-0.5 w-2.5 rounded-full bg-chart-1" aria-hidden />
                Clicks
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-0.5 w-2.5 rounded-full bg-chart-2" aria-hidden />
                Unique visitors
              </span>
            </div>
            <div className="px-3 pb-3 pt-2">
              {trendData.length > 0 ? (
                <ClicksTrendChart trendData={trendData} period={state.period} />
              ) : (
                <CardEmpty>No trend data available for this period</CardEmpty>
              )}
            </div>
          </RaisedCard>

          <RaisedCard className={isRefreshing ? 'opacity-70 transition-opacity' : undefined}>
            <CardHead title="Clicks by page">
              <div className="relative w-full sm:w-60">
                <HugeiconsIcon icon={Search01Icon} size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search pages or elements"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="h-8 pl-8 text-xs"
                />
              </div>
            </CardHead>

            {availableGroups.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 px-[18px] pb-3">
                <FilterChip active={selectedGroups.has('all')} onClick={() => toggleGroup('all')}>
                  All <span className="tabular-nums opacity-70">{clickData.length}</span>
                </FilterChip>
                {availableGroups.map((group) => (
                  <FilterChip key={group} active={selectedGroups.has(group)} onClick={() => toggleGroup(group)}>
                    <span className="capitalize">{group}</span>
                    <span className="tabular-nums opacity-70">{groupSummary[group].pages}</span>
                  </FilterChip>
                ))}
              </div>
            )}

            <div className="flex border-y border-border/60 bg-muted/40 px-[18px] py-2 text-xs text-muted-foreground">
              <span className="flex-1 pl-6">Page</span>
              <span className="w-[90px] shrink-0 text-right">Clicks</span>
              <span className="hidden w-[220px] shrink-0 pl-6 sm:block">Top target</span>
              <span className="hidden w-[110px] shrink-0 text-right md:block">Unique visitors</span>
              <span className="w-[110px] shrink-0 whitespace-nowrap text-right">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="inline-flex items-center gap-1" aria-label="How clicks per visitor is calculated">
                        Clicks / visitor
                        <HugeiconsIcon icon={InformationCircleIcon} size={12} className="text-muted-foreground/60" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed">
                      {CTR_TOOLTIP}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </span>
            </div>

            {paginatedData.length === 0 ? (
              <CardEmpty>{debouncedSearch || searchTerm ? 'No clicks match your search' : 'No click data available'}</CardEmpty>
            ) : (
              <div className="flex flex-col">
                {paginatedData.map((page, index) => {
                  const rowTarget = page.top_targets[0]
                  const isExpanded = selectedPage === page.page
                  return (
                    <div key={`row-${index}`} className="border-b border-border/60 last:border-b-0">
                      <button
                        type="button"
                        onClick={() => togglePageDetails(page.page)}
                        aria-expanded={isExpanded}
                        className="group flex min-h-12 w-full items-center px-[18px] py-2 text-left transition-colors hover:bg-muted/40"
                      >
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                          <HugeiconsIcon
                            icon={ArrowDown01Icon}
                            size={14}
                            className={`shrink-0 text-muted-foreground transition-transform duration-150 group-hover:text-foreground ${isExpanded ? 'rotate-180' : ''}`}
                          />
                          <span className="truncate text-[13px] font-medium text-foreground" title={page.page || '/'}>{page.page || '/'}</span>
                          <span className="shrink-0 rounded-[5px] bg-muted px-1.5 py-px text-xs text-muted-foreground">
                            {sentenceLabel(page.page_group)}
                          </span>
                        </span>
                        <span className="w-[90px] shrink-0 text-right text-[13px] font-medium text-foreground tabular-nums">
                          {formatNumeric(page.total_clicks)}
                        </span>
                        <span className="hidden w-[220px] shrink-0 truncate pl-6 font-mono text-xs text-muted-foreground sm:block" title={rowTarget?.target}>
                          {targetLabel(rowTarget?.target)}
                        </span>
                        <span className="hidden w-[110px] shrink-0 text-right text-[13px] text-foreground tabular-nums md:block">
                          {formatNumeric(page.unique_sessions)}
                        </span>
                        <span className="w-[110px] shrink-0 text-right text-[13px] text-foreground tabular-nums">
                          {page.unique_sessions > 0 ? (page.total_clicks / page.unique_sessions).toFixed(2) : '—'}
                        </span>
                      </button>

                      {isExpanded && (
                        <div className="border-t border-border/60 bg-muted/40 px-[18px] py-4" onClick={(e) => e.stopPropagation()}>
                          {isLoadingDetails ? (
                            <div className="space-y-3">
                              <Skeleton className="h-8 w-full" />
                              <Skeleton className="h-32 w-full" />
                            </div>
                          ) : clickDetails ? (
                            <div className="flex flex-col gap-4">
                              <div className="flex flex-wrap gap-x-6 gap-y-2">
                                {[
                                  { label: 'Clicks', value: formatNumeric(clickDetails.summary.total_clicks) },
                                  { label: 'Unique visitors', value: formatNumeric(clickDetails.summary.unique_sessions) },
                                  { label: 'Total views', value: formatNumeric(clickDetails.summary.page_views) },
                                  { label: 'Click-through rate', value: `${(clickDetails.summary.click_through_rate * 100).toFixed(1)}%` },
                                ].map((m) => (
                                  <div key={m.label} className="flex flex-col gap-0.5">
                                    <span className="text-xs text-muted-foreground">{m.label}</span>
                                    <span className="text-sm font-semibold text-foreground tabular-nums">{m.value}</span>
                                  </div>
                                ))}
                              </div>

                              {clickDetails.data.clicks.length > 0 && (
                                <div className="surface-raised flex flex-col">
                                  <div className="flex items-center justify-between gap-2 px-[18px] py-3">
                                    <h4 className="text-[13px] font-semibold text-foreground">Top clicked elements</h4>
                                    {clickDetails.meta.total_count > clicksLimit && (
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground tabular-nums">
                                          {clicksOffset + 1} to {Math.min(clicksOffset + clicksLimit, clickDetails.meta.total_count)} of {clickDetails.meta.total_count}
                                        </span>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={(e) => { e.stopPropagation(); setClicksOffset(o => Math.max(0, o - clicksLimit)) }}
                                          disabled={clicksOffset === 0}
                                          className="h-6 px-2 text-xs"
                                        >
                                          Prev
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={(e) => { e.stopPropagation(); setClicksOffset(o => o + clicksLimit) }}
                                          disabled={clicksOffset + clicksLimit >= clickDetails.meta.total_count}
                                          className="h-6 px-2 text-xs"
                                        >
                                          Next
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                  {clickDetails.data.clicks.map((click, idx) => {
                                    const getElementTypeInfo = (type: string | null) => {
                                      switch (type) {
                                        case 'link': return { icon: Link01Icon, label: 'Link' }
                                        case 'button': return { icon: Cursor02Icon, label: 'Button' }
                                        case 'nav-link': return { icon: Menu01Icon, label: 'Nav' }
                                        default: return { icon: MouseLeftClick01Icon, label: 'Element' }
                                      }
                                    }
                                    const isKnownType = click.secondary && ['link', 'button', 'nav-link'].includes(click.secondary)
                                    const elementInfo = getElementTypeInfo(click.secondary)

                                    return (
                                      <div key={idx} className="flex items-center gap-4 border-t border-border/60 px-[18px] py-2.5">
                                        <div className="min-w-0 flex-1">
                                          <p className="truncate text-[13px] font-medium text-foreground">{click.target}</p>
                                          <div className="mt-1 flex min-w-0 items-center gap-2">
                                            <span className="inline-flex h-5 shrink-0 items-center gap-1 rounded-[5px] bg-muted px-1.5 text-xs text-muted-foreground">
                                              <HugeiconsIcon icon={elementInfo.icon} size={10} />
                                              {elementInfo.label}
                                            </span>
                                            {!isKnownType && click.secondary && (
                                              <span className="truncate text-xs text-muted-foreground">{click.secondary}</span>
                                            )}
                                            {isNavigationClick(click.target, click.secondary) ? (
                                              <span className="inline-flex h-5 shrink-0 items-center rounded-[5px] bg-muted px-1.5 text-xs text-foreground">Navigation</span>
                                            ) : (
                                              <span className="inline-flex h-5 shrink-0 items-center rounded-[5px] px-1.5 text-xs text-muted-foreground ring-1 ring-inset ring-border">On the page</span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="shrink-0 text-right">
                                          <p className="text-[13px] font-semibold text-foreground tabular-nums">{formatNumeric(click.clicks)}</p>
                                          <p className="text-xs text-muted-foreground tabular-nums">
                                            {formatNumeric(click.unique_sessions)} unique visitors
                                          </p>
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="py-6 text-center text-[13px] text-muted-foreground">Failed to load click details</p>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {totalPages > 1 && (
              <Pagination
                from={(currentPage - 1) * itemsPerPage + 1}
                to={Math.min(currentPage * itemsPerPage, paginationTotal)}
                total={paginationTotal}
                extra={isFetchingMore ? <span className="ml-2">(loading…)</span> : null}
                onPrev={() => handlePageChange(Math.max(1, currentPage - 1))}
                onNext={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                prevDisabled={currentPage === 1}
                nextDisabled={currentPage === totalPages || isFetchingMore}
              />
            )}
          </RaisedCard>
        </div>

        <FormsSection />
      </div>
    </div>
  )
}
